import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import {
  useDeleteHazardRiskReductionStepsForMeasure,
  useDeleteRiskReductionMeasure,
  useGetRiskReductionMeasure,
  useListSafetyFunctions,
  useUpdateRiskReductionMeasure,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { NotFoundView } from '@/features/errors/not-found-view'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { useMachineryDirectiveEhsrList } from '../../hooks/useMachineryDirectiveEhsrList'
import { AddEhsrsToRrmDialog } from './AddEhsrsToRrmDialog'
import { AssignSafetyFunctionDialog } from './AssignSafetyFunctionDialog'
import { DeleteRiskReductionMeasureDialog } from './DeleteRiskReductionMeasureDialog'
import { RemoveEhsrFromRrmDialog } from './RemoveEhsrFromRrmDialog'
import { RiskReductionMeasureDetailInfoWidgets } from './RiskReductionMeasureDetailInfoWidgets'
import type { RiskReductionMeasureFormData } from './RiskReductionMeasureForm.schema'
import { RiskReductionMeasureLinkedEhsrsSection } from './RiskReductionMeasureLinkedEhsrsSection'
import { RiskReductionMeasureLinkedHazardsSection } from './RiskReductionMeasureLinkedHazardsSection'
import { RiskReductionMeasureLinkedSafetyFunctionsSection } from './RiskReductionMeasureLinkedSafetyFunctionsSection'
import { RiskReductionMeasureImplementationSection } from './RiskReductionMeasureImplementationSection'
import { UnlinkRrmFromHazardDialog } from './UnlinkRrmFromHazardDialog'
import { UnlinkSafetyFunctionFromRrmDialog } from './UnlinkSafetyFunctionFromRrmDialog'
import {
  getLinkedSafetyFunctionDisplay,
  isSafetyFunctionMeasure,
  toEhsrClauseIdsUpdateInput,
  toRiskReductionMeasureInput,
  toSafetyFunctionAssignmentInput,
} from './riskReductionMeasuresUtils'

export function RiskReductionMeasureDetailPage() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
    riskReductionMeasureId: string
  }>()
  const complianceId = params.machineryDirectiveComplianceId
  const productId = params.productId
  const riskReductionMeasureId = params.riskReductionMeasureId
  const navigate = useNavigate()

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false)
  const [isAddEhsrsDialogOpen, setIsAddEhsrsDialogOpen] = useState(false)
  const [hazardPendingUnlink, setHazardPendingUnlink] = useState<string | null>(
    null
  )
  const [unlinkingHazardId, setUnlinkingHazardId] = useState<string | null>(
    null
  )
  const [isUnlinkingSafetyFunction, setIsUnlinkingSafetyFunction] =
    useState(false)
  const [
    isSafetyFunctionUnlinkDialogOpen,
    setIsSafetyFunctionUnlinkDialogOpen,
  ] = useState(false)
  const [ehsrPendingRemoval, setEhsrPendingRemoval] = useState<string | null>(
    null
  )
  const [removingEhsrClauseId, setRemovingEhsrClauseId] = useState<
    string | null
  >(null)
  const [isSavingEhsrs, setIsSavingEhsrs] = useState(false)

  const {
    data: riskReductionMeasure,
    isLoading,
    error,
  } = useGetRiskReductionMeasure(complianceId, riskReductionMeasureId)
  const updateMutation = useUpdateRiskReductionMeasure(complianceId ?? '')
  const deleteMutation = useDeleteRiskReductionMeasure(complianceId ?? '')
  const unlinkFromHazardMutation = useDeleteHazardRiskReductionStepsForMeasure(
    complianceId ?? '',
    riskReductionMeasureId ?? ''
  )
  const ehsrOptions = useMachineryDirectiveEhsrList()

  const { t } = useTranslation('machineryDirectiveCompliance')

  const clauseById = useMemo(
    () => new Map(ehsrOptions.map((opt) => [opt.id, opt])),
    [ehsrOptions]
  )

  const hasLinkedSafetyFunction =
    riskReductionMeasure?.safetyFunctionId != null &&
    riskReductionMeasure.safetyFunctionId.trim() !== ''

  const { data: safetyFunctions = [] } = useListSafetyFunctions(complianceId)

  const linkedSafetyFunction = useMemo(() => {
    const safetyFunctionId = riskReductionMeasure?.safetyFunctionId
    if (safetyFunctionId == null || safetyFunctionId.trim() === '') {
      return null
    }

    const display = getLinkedSafetyFunctionDisplay(
      safetyFunctionId,
      safetyFunctions
    )
    if (display == null) {
      return null
    }

    const safetyFunction = safetyFunctions.find(
      (item) => item.id === safetyFunctionId
    )

    return {
      id: safetyFunctionId,
      name: display.name,
      eplanReferenceId: display.eplanReferenceId,
      description: safetyFunction?.description ?? null,
    }
  }, [riskReductionMeasure?.safetyFunctionId, safetyFunctions])

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true)
  }

  const handleAssignClick = () => {
    setIsAssignDialogOpen(true)
  }

  const handleAssignSave = (safetyFunctionId: string | null) => {
    if (
      !riskReductionMeasureId ||
      !complianceId ||
      riskReductionMeasure == null
    ) {
      return
    }

    updateMutation.mutate(
      {
        riskReductionMeasureId,
        ...toSafetyFunctionAssignmentInput(
          riskReductionMeasure,
          safetyFunctionId
        ),
      },
      {
        onSuccess: () => {
          toast.success(
            t('riskReductionMeasuresLibraryPage.assignDialog.success')
          )
          setIsAssignDialogOpen(false)
        },
        onError: (err) => {
          toast.error(
            err.message ??
              t('riskReductionMeasuresLibraryPage.saveFailureUpdate')
          )
        },
      }
    )
  }

  const handleConfirmDelete = async () => {
    const hasLinkedHazards =
      (riskReductionMeasure?.linkedHazards?.length ?? 0) > 0
    if (!riskReductionMeasureId || !complianceId || hasLinkedHazards) {
      return
    }
    try {
      await deleteMutation.mutateAsync(riskReductionMeasureId)
      toast.success(t('riskReductionMeasuresLibraryPage.deleteSuccess'))
      setIsDeleteDialogOpen(false)
      navigate('..', { relative: 'path' })
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('riskReductionMeasuresLibraryPage.deleteFailure')
      toast.error(message)
    }
  }

  const handleUnlinkClick = (hazardId: string) => {
    setHazardPendingUnlink(hazardId)
  }

  const handleCloseUnlinkDialog = () => {
    if (unlinkFromHazardMutation.isPending) {
      return
    }
    setHazardPendingUnlink(null)
  }

  const handleUnlinkSafetyFunctionClick = () => {
    setIsSafetyFunctionUnlinkDialogOpen(true)
  }

  const handleCloseSafetyFunctionUnlinkDialog = () => {
    if (isUnlinkingSafetyFunction) {
      return
    }
    setIsSafetyFunctionUnlinkDialogOpen(false)
  }

  const handleConfirmUnlinkSafetyFunction = () => {
    if (
      !riskReductionMeasureId ||
      !complianceId ||
      riskReductionMeasure == null
    ) {
      return
    }

    setIsUnlinkingSafetyFunction(true)
    updateMutation.mutate(
      {
        riskReductionMeasureId,
        ...toSafetyFunctionAssignmentInput(riskReductionMeasure, null),
      },
      {
        onSuccess: () => {
          toast.success(
            t(
              'riskReductionMeasuresLibraryPage.rrmDetail.unlinkSafetyFunctionSuccess'
            )
          )
          setIsSafetyFunctionUnlinkDialogOpen(false)
        },
        onError: (err) => {
          toast.error(
            err.message ??
              t(
                'riskReductionMeasuresLibraryPage.rrmDetail.unlinkSafetyFunctionError'
              )
          )
        },
        onSettled: () => {
          setIsUnlinkingSafetyFunction(false)
        },
      }
    )
  }

  const handleConfirmUnlink = async () => {
    if (!hazardPendingUnlink) {
      return
    }
    setUnlinkingHazardId(hazardPendingUnlink)
    try {
      await unlinkFromHazardMutation.mutateAsync({
        hazardId: hazardPendingUnlink,
      })
      toast.success(
        t('riskReductionMeasuresLibraryPage.rrmDetail.unlinkSuccess')
      )
      setHazardPendingUnlink(null)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('riskReductionMeasuresLibraryPage.rrmDetail.unlinkError')
      toast.error(message)
    } finally {
      setUnlinkingHazardId(null)
    }
  }

  const handleRemoveEhsrClick = (clauseId: string) => {
    setEhsrPendingRemoval(clauseId)
  }

  const handleAddEhsrsClick = () => {
    setIsAddEhsrsDialogOpen(true)
  }

  const handleSaveEhsrs = (nextEhsrClauseIds: string[]) => {
    if (
      !riskReductionMeasureId ||
      !complianceId ||
      riskReductionMeasure == null
    ) {
      return
    }

    setIsSavingEhsrs(true)
    updateMutation.mutate(
      {
        riskReductionMeasureId,
        ...toEhsrClauseIdsUpdateInput(riskReductionMeasure, nextEhsrClauseIds),
      },
      {
        onSuccess: () => {
          toast.success(t('riskReductionMeasuresLibraryPage.updateSuccess'))
          setIsAddEhsrsDialogOpen(false)
        },
        onError: (err) => {
          toast.error(
            err.message ??
              t('riskReductionMeasuresLibraryPage.saveFailureUpdate')
          )
        },
        onSettled: () => {
          setIsSavingEhsrs(false)
        },
      }
    )
  }

  const handleCloseRemoveEhsrDialog = () => {
    if (removingEhsrClauseId != null) {
      return
    }
    setEhsrPendingRemoval(null)
  }

  const handleConfirmRemoveEhsr = () => {
    if (
      !riskReductionMeasureId ||
      !complianceId ||
      riskReductionMeasure == null ||
      ehsrPendingRemoval == null
    ) {
      return
    }

    const nextEhsrClauseIds = (riskReductionMeasure.ehsrClauseIds ?? []).filter(
      (clauseId) => clauseId !== ehsrPendingRemoval
    )

    setRemovingEhsrClauseId(ehsrPendingRemoval)
    updateMutation.mutate(
      {
        riskReductionMeasureId,
        ...toEhsrClauseIdsUpdateInput(riskReductionMeasure, nextEhsrClauseIds),
      },
      {
        onSuccess: () => {
          toast.success(
            t('riskReductionMeasuresLibraryPage.rrmDetail.removeEhsrSuccess')
          )
          setEhsrPendingRemoval(null)
        },
        onError: (err) => {
          toast.error(
            err.message ??
              t('riskReductionMeasuresLibraryPage.rrmDetail.removeEhsrError')
          )
        },
        onSettled: () => {
          setRemovingEhsrClauseId(null)
        },
      }
    )
  }

  const pendingEhsrLabel = useMemo(() => {
    if (ehsrPendingRemoval == null) {
      return null
    }
    const clause = clauseById.get(ehsrPendingRemoval)
    return clause?.selectedLabel ?? ehsrPendingRemoval
  }, [clauseById, ehsrPendingRemoval])

  const handleFieldUpdate = (
    data: RiskReductionMeasureFormData,
    onSuccess?: () => void
  ) => {
    if (!riskReductionMeasureId) {
      return
    }

    updateMutation.mutate(
      {
        riskReductionMeasureId,
        ...toRiskReductionMeasureInput(data),
      },
      {
        onSuccess: () => {
          toast.success(t('riskReductionMeasuresLibraryPage.updateSuccess'))
          onSuccess?.()
        },
        onError: (err) => {
          toast.error(
            err.message ??
              t('riskReductionMeasuresLibraryPage.saveFailureUpdate')
          )
        },
      }
    )
  }

  if (complianceId == null || riskReductionMeasureId == null) {
    return null
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Skeleton className='h-10 w-48' />
        <Skeleton className='h-64 w-full max-w-xl' />
      </div>
    )
  }

  if (error || !riskReductionMeasure) {
    const isNotFound =
      error == null || (isAxiosError(error) && error.response?.status === 404)

    return (
      <div className='flex flex-1 flex-col items-center justify-center py-16'>
        <NotFoundView
          showCode={false}
          title={t('riskReductionMeasuresLibraryPage.rrmDetail.notFound')}
          description={isNotFound ? undefined : error?.message}
          primaryAction={{
            label: t(
              'riskReductionMeasuresLibraryPage.rrmDetail.backToLibrary'
            ),
            onClick: () => navigate('..', { relative: 'path' }),
          }}
        />
      </div>
    )
  }

  const isMutationPending =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    unlinkFromHazardMutation.isPending ||
    isUnlinkingSafetyFunction ||
    removingEhsrClauseId != null ||
    isSavingEhsrs
  const ehsrIds = riskReductionMeasure.ehsrClauseIds ?? []
  const isEligibleForPlr = isSafetyFunctionMeasure(riskReductionMeasure)
  const linkedHazards = riskReductionMeasure.linkedHazards ?? []

  return (
    <>
      <div className='sticky top-0 z-10 border-b bg-background/95 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
        <ComplianceSubPageHeader className='mb-0'>
          <ComplianceSubPageTitle className='flex items-center gap-2 truncate'>
            <Link
              to='..'
              relative='path'
              className='text-sm text-muted-foreground hover:text-foreground'
            >
              <ArrowLeft className='size-4' />
            </Link>
            {riskReductionMeasure.name}
          </ComplianceSubPageTitle>
          <div className='flex shrink-0 gap-2'>
            {isEligibleForPlr && hasLinkedSafetyFunction ? (
              <Button
                variant='outline'
                size='sm'
                onClick={handleAssignClick}
                disabled={isMutationPending}
              >
                {t(
                  'riskReductionMeasuresLibraryPage.actions.changeSafetyFunction'
                )}
              </Button>
            ) : null}
            <Button
              variant='destructive'
              size='sm'
              onClick={handleDeleteClick}
              disabled={isMutationPending}
            >
              {t('riskReductionMeasuresLibraryPage.rrmDetail.delete')}
            </Button>
          </div>
        </ComplianceSubPageHeader>
      </div>

      <div className='space-y-8 pt-4'>
        <RiskReductionMeasureDetailInfoWidgets
          riskReductionMeasure={riskReductionMeasure}
          isUpdating={updateMutation.isPending}
          onUpdate={handleFieldUpdate}
        />

        <RiskReductionMeasureImplementationSection
          complianceId={complianceId}
          productId={productId}
          riskReductionMeasure={riskReductionMeasure}
          isUpdating={updateMutation.isPending}
        />

        <RiskReductionMeasureLinkedEhsrsSection
          ehsrClauseIds={ehsrIds}
          clauseById={clauseById}
          removingClauseId={removingEhsrClauseId}
          onRemoveClick={handleRemoveEhsrClick}
          onAddClick={handleAddEhsrsClick}
          isAddDisabled={isMutationPending}
        />

        {isEligibleForPlr && (
          <RiskReductionMeasureLinkedSafetyFunctionsSection
            linkedSafetyFunction={linkedSafetyFunction}
            isUnlinking={isUnlinkingSafetyFunction}
            onUnlinkClick={handleUnlinkSafetyFunctionClick}
            onAssignClick={handleAssignClick}
            isAssignDisabled={isMutationPending}
          />
        )}

        <RiskReductionMeasureLinkedHazardsSection
          linkedHazards={linkedHazards}
          unlinkingHazardId={unlinkingHazardId}
          onUnlinkClick={handleUnlinkClick}
        />
      </div>

      <AddEhsrsToRrmDialog
        isOpen={isAddEhsrsDialogOpen}
        onClose={() => setIsAddEhsrsDialogOpen(false)}
        ehsrOptions={ehsrOptions}
        ehsrClauseIds={ehsrIds}
        onSave={handleSaveEhsrs}
        isSaving={isSavingEhsrs}
      />

      <AssignSafetyFunctionDialog
        isOpen={isAssignDialogOpen}
        onClose={() => setIsAssignDialogOpen(false)}
        onSave={handleAssignSave}
        riskReductionMeasure={riskReductionMeasure}
        safetyFunctions={safetyFunctions}
        isSaving={updateMutation.isPending}
      />

      <DeleteRiskReductionMeasureDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
        hasLinkedHazards={linkedHazards.length > 0}
      />

      <UnlinkRrmFromHazardDialog
        isOpen={hazardPendingUnlink != null}
        onClose={handleCloseUnlinkDialog}
        onConfirm={handleConfirmUnlink}
        isUnlinking={unlinkFromHazardMutation.isPending}
      />

      <UnlinkSafetyFunctionFromRrmDialog
        isOpen={isSafetyFunctionUnlinkDialogOpen}
        onClose={handleCloseSafetyFunctionUnlinkDialog}
        onConfirm={handleConfirmUnlinkSafetyFunction}
        isUnlinking={isUnlinkingSafetyFunction}
      />

      <RemoveEhsrFromRrmDialog
        isOpen={ehsrPendingRemoval != null}
        ehsrLabel={pendingEhsrLabel}
        onClose={handleCloseRemoveEhsrDialog}
        onConfirm={handleConfirmRemoveEhsr}
        isRemoving={removingEhsrClauseId != null}
      />
    </>
  )
}
